import { z } from 'zod';

import {
  clearRootDomainCookie,
  getCookieValue,
  resolveRootCookieDomain,
  writeRootDomainCookie,
} from './root-domain-cookie';

export const PORTFOLIO_ATTRIBUTION_COOKIE_NAME = 'portfolio_attribution';
export const PORTFOLIO_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

const MAX_REFERRER_LENGTH = 512;
const MAX_UTM_CONTENT_LENGTH = 120;

const portfolioAttributionTouchShape = {
  last_touch_portfolio_code: z.string().min(1),
  portfolio_url: z.string().url().max(MAX_REFERRER_LENGTH),
  utm_source: z.literal('portfolio'),
  utm_medium: z.literal('referral'),
  utm_campaign: z.string().min(1).max(64),
  utm_content: z.string().max(MAX_UTM_CONTENT_LENGTH).nullable(),
  referrer: z.string().max(MAX_REFERRER_LENGTH).nullable(),
  landed_at: z.string().datetime(),
};

export const PortfolioAttributionSchema = z.object({
  first_touch_portfolio_code: z.string().min(1),
  ...portfolioAttributionTouchShape,
});

export type PortfolioAttribution = z.infer<typeof PortfolioAttributionSchema>;

export type PortfolioAttributionAnalyticsProperties = Record<string, unknown> & {
  last_touch_portfolio_code: string;
  first_touch_portfolio_code: string;
  portfolio_utm_source: 'portfolio';
  portfolio_utm_medium: 'referral';
  portfolio_utm_campaign: string;
  portfolio_utm_content: string | null;
  portfolio_referrer: string | null;
  portfolio_landed_at: string;
};

export function sanitizeAttributionReferrer(referrer: string | null | undefined): string | null {
  if (!referrer) {
    return null;
  }

  try {
    const url = new URL(referrer);
    const sanitized = `${url.origin}${url.pathname}`;
    return sanitized.slice(0, MAX_REFERRER_LENGTH);
  } catch {
    return null;
  }
}

export function buildPortfolioAttribution(input: {
  portfolioCode: string;
  firstTouchPortfolioCode?: string;
  portfolioUrl: string;
  utmContent?: string | null;
  referrer?: string | null;
  landedAt?: string;
}): PortfolioAttribution {
  const utmContent = input.utmContent?.trim() ? input.utmContent.trim().slice(0, MAX_UTM_CONTENT_LENGTH) : null;

  return PortfolioAttributionSchema.parse({
    first_touch_portfolio_code: input.firstTouchPortfolioCode ?? input.portfolioCode,
    last_touch_portfolio_code: input.portfolioCode,
    portfolio_url: input.portfolioUrl,
    utm_source: 'portfolio',
    utm_medium: 'referral',
    utm_campaign: input.portfolioCode,
    utm_content: utmContent,
    referrer: sanitizeAttributionReferrer(input.referrer),
    landed_at: input.landedAt ?? new Date().toISOString(),
  });
}

export function serializePortfolioAttribution(attribution: PortfolioAttribution): string {
  return JSON.stringify(attribution);
}

export function parsePortfolioAttribution(raw: string | null | undefined): PortfolioAttribution | null {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    const result = PortfolioAttributionSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function readPortfolioAttributionCookie(
  cookieSource = typeof document === 'undefined' ? '' : document.cookie,
): PortfolioAttribution | null {
  return parsePortfolioAttribution(getCookieValue(PORTFOLIO_ATTRIBUTION_COOKIE_NAME, cookieSource));
}

export function writePortfolioAttributionCookie(
  attribution: PortfolioAttribution,
  hostname = typeof window === 'undefined' ? '' : window.location.hostname,
): void {
  writeRootDomainCookie({
    name: PORTFOLIO_ATTRIBUTION_COOKIE_NAME,
    value: serializePortfolioAttribution(attribution),
    maxAgeSeconds: PORTFOLIO_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS,
    hostname,
  });
}

export function clearPortfolioAttributionCookie(
  hostname = typeof window === 'undefined' ? '' : window.location.hostname,
): void {
  clearRootDomainCookie({
    name: PORTFOLIO_ATTRIBUTION_COOKIE_NAME,
    hostname,
  });
}

export function capturePortfolioAttribution(input: {
  portfolioCode: string;
  portfolioUrl: string;
  utmContent?: string | null;
  referrer?: string | null;
  hostname?: string;
  cookieSource?: string;
}): PortfolioAttribution {
  const existing = readPortfolioAttributionCookie(input.cookieSource);

  const attribution = buildPortfolioAttribution({
    portfolioCode: input.portfolioCode,
    firstTouchPortfolioCode: existing?.first_touch_portfolio_code,
    portfolioUrl: input.portfolioUrl,
    utmContent: input.utmContent,
    referrer: input.referrer,
  });

  writePortfolioAttributionCookie(attribution, input.hostname);
  return attribution;
}

// Namespaced as portfolio_utm_* so these never collide with the real organic/paid
// utm_source/utm_medium/utm_campaign/utm_content properties tracked separately via
// useUtmParams() — both get spread into the same identify()/track() properties object.
export function toPortfolioAttributionAnalyticsProperties(
  attribution: PortfolioAttribution,
): PortfolioAttributionAnalyticsProperties {
  return {
    last_touch_portfolio_code: attribution.last_touch_portfolio_code,
    first_touch_portfolio_code: attribution.first_touch_portfolio_code,
    portfolio_utm_source: attribution.utm_source,
    portfolio_utm_medium: attribution.utm_medium,
    portfolio_utm_campaign: attribution.utm_campaign,
    portfolio_utm_content: attribution.utm_content,
    portfolio_referrer: attribution.referrer,
    portfolio_landed_at: attribution.landed_at,
  };
}

export function resolvePortfolioAttributionCookieDomain(hostname: string): string | null {
  return resolveRootCookieDomain(hostname);
}
